import type {
  ActionName, GranuleLevel, ScaleLevel, RoundReading, RunStep, Diagnosis,
} from '../types'

/*
 * 背景：管线（ripple_restore.py）归属 Asterove_yeoui 仓库，按该仓库的语言政策，
 *   决策理由以韩语产出，例如「격자 노치(무손상)」「전폭 叠瓦 지수 23.2% (structured)…」。
 *   本页面向米羊的中文用户，把韩语原样渲染出去等于没做完。
 * 设计意图：不在管线里改语言（那会动到论文所依赖的产物），而是加一层解释。
 *   解释按 action 取模板，再用该步 result 里的真实数值填充，
 *   于是页面上的每个数字都来自管线实测，措辞由我们负责。
 * 关键约束：
 *   1. 这里说的是「这一步在做什么」。「为什么选这一步」由服务端换成中文之后随 step.reason
 *      一起送来（lab_ripple_api 的 core/step_reason.py），两句话不重复。
 *      2026-09-10 用户裁决：韩语一个字都不许到浏览器，原先折叠在「技术细节」里的
 *      管线原文也不留了；它还在服务端盘上，排查的人对得上。
 *   2. 认不出的动作要能落地。管线的动作集会长（`request_human` 就是后加的），
 *      而 ACTION_INFO 少一项就让整条时间线抛异常，比少一句解释严重得多。
 */

/** 判定等级的中文标签与严重度。severity 决定卡片配色。 */
export type Severity = 'none' | 'warn' | 'bad'

export const GRANULE_LABEL: Record<GranuleLevel, { text: string; severity: Severity }> = {
  none: { text: '未检出', severity: 'none' },
  suspected: { text: '疑似', severity: 'warn' },
  flat: { text: '平坦区可见', severity: 'warn' },
  pervasive: { text: '全幅可见', severity: 'bad' },
}

export const SCALE_LABEL: Record<ScaleLevel, { text: string; severity: Severity }> = {
  none: { text: '未检出', severity: 'none' },
  suspected: { text: '疑似', severity: 'warn' },
  structured: { text: '结构化', severity: 'bad' },
}

/**
 * 三项指标的统一称呼与固定次序。
 *
 * 背景：2026-09-12 用户指着完成页画了个叉——上面那排复诊读数是「叠瓦指数 /
 *   平坦区鳞片 / 格子纹」，下面那排诊断卡是「格子纹 / 鳞片纹理 / 叠瓦指数」，
 *   同样的三项、两种顺序、还有两套名字（平坦区鳞片 vs 鳞片纹理），
 *   于是同一项在一页上要找两次：「排序还不太对」「感觉有点重复」。
 * 设计意图：称呼与次序都只有一份，页面各处引用它，不再各写一遍。
 *   次序按「它决定了什么」排：叠瓦指数决定这张图是清一清还是只能整幅重画
 *   （见 METRIC_INTRO.scale），鳞片次之，格子纹是能无损切掉的那一项，排末。
 * 关键约束：次序**固定**，不按本次的严重度重排——卡片位置跟着每次运行变，
 *   用户就没法在两次运行之间对着同一个位置比。
 */
export const METRIC_ORDER = ['scale', 'granule', 'lattice'] as const

export const METRIC_LABEL: Record<(typeof METRIC_ORDER)[number], string> = {
  scale: '叠瓦指数',
  granule: '鳞片纹理',
  lattice: '格子纹',
}

/**
 * 三项指标各自在说什么。
 *
 * 2026-09-11 用户裁决：「这种都要说人话才行」。原先这三句里混着「固定周期」
 * 「上采样」「分块统计」——那是论文里的说法，读它的人已经知道答案了，
 * 而这一页要答的是「我的图怎么了」。所以：先说它长什么样、在画面的哪儿看得见，
 * 再说它要紧在哪。数字一个不改（都来自管线产物），改的只是称呼。
 */
export const METRIC_INTRO = {
  lattice: '整张图上罩着一层极细的网格，格眼只有两三个像素，缩小看不出来，放大或印出来就显脏。它是出图时留下的。',
  granule: '天空、墙面、皮肤这种本该干净的地方，有没有冒出一层颗粒或者鱼鳞似的纹路。',
  scale: '把画面切成小方块一块块数，有多少块长着排列整齐的鱼鳞。这一项决定了这张图是顺手清一清就行，还是只能整幅重画。',
} as const

/**
 * 动作的中文名、它跑着的时候那句话、以及它事后的说明。
 *
 * 背景：用户看到「reference_clean」不知道那是什么，看到韩语更不知道。
 * 设计意图：`doing` 与 `what` 分开，因为它们出现在两个不同的时刻，说的不是同一件事。
 *   · `doing` 压在图上，用户正盯着它等（见 YourImage 的玻璃条）。这时他要知道的是
 *     **此刻在找什么、要等多久**，不是这个动作的定义。
 *   · `what` 用在事后的轨迹里，作为取不到实测数值时的兜底说明。
 * 关键约束：
 *   1. `doing` 里不写「不动你的图」「属于无损处理」这类自辩。跑着的时候用户又不能
 *      拿它做任何决定，而每一句都缀一条免责，整条进度读起来像在心虚。会不会改画面
 *      属于要拍板的事，它在闸上说（见 Gates），那才是用户能据此做决定的位置。
 *   2. 会花时间的那一步要说清时间花在哪（深度修复要走一次外部出图）。页面只给已耗时、
 *      不给进度条，那句「通常要几十秒」就是用户唯一的尺子。
 *   3. `doing` 不引用 result：它在这一步跑完之前就得说话，那时 result 还是空的。
 */
export const ACTION_INFO: Record<ActionName, { label: string; doing: string; what: string }> = {
  diagnose: {
    label: '诊断',
    // 说在找什么，不说怎么找。「全幅原像素、不缩放」在图下那行说明里已经写着了
    // （见 RipplePage 的 caption），这里重复一遍等于两处都在说机器怎么干活。
    //
    // 也不报数量。原先这句是「在找三样东西：……」，用户第一眼问的是
    // 「开头什么是正在找三个东西？」（2026-09-12）——他要的是「在看我的图哪儿脏」，
    // 先数个数就得先猜那三样指什么，等于给他出了道题。
    doing: '在看画面上有没有细网格、颗粒和鱼鳞纹。',
    what: '把整幅图按原像素量一遍，再切成小块逐块统计。',
  },
  notch: {
    label: '格子切除',
    // 「在频谱上」是我们怎么干的，用户此刻既看不见频谱也用不上它。
    doing: '在把那层细网格挑出来清掉。',
    what: '只把那层规则的细网格挑出来清掉，画面内容不变，属于无损处理。',
  },
  iso_clean: {
    label: '局部净化',
    doing: '只清刚才查出问题的那几块。',
    what: '只对判定为异常的区域做各向性平滑，其余像素原样保留。',
  },
  reference_clean: {
    label: '参考级净化',
    doing: '在清出一张干净的参考本，给下一步重画当依据。',
    what: '先把图净化到可以当参考本的程度。这一步的产物不是成品，它只用来给下一步当唯一依据。',
  },
  /*
   * 对用户叫「深度修复」，不叫「重新生成」（2026-09-09 用户裁决，同 Gates.tsx 的注释）。
   * label 是用户在时间线上读到的词，必须与授权闸上的按钮一致，
   * 否则他授权的是「深度修复」，而结果页说做了「重新生成」，看起来像做了别的事。
   * action 名（regenerate）不动，它要与管线产物对得上。
   */
  regenerate: {
    label: '深度修复',
    // 「在外部出图」是我们的分工，用户要的只是那个「几十秒」——页面不给进度条，
    // 这句就是他唯一的尺子。
    doing: '照参考本重画整幅画面，通常要几十秒。',
    // 事后那一句用过去时，且不再提授权：轨迹是给已经点过同意的人读的，
    // 「需要你先授权」摆在那儿像是还欠一步。
    what: '以参考本为唯一依据，把整幅画面重画了一遍。',
  },
  /*
   * 管线判「这张图只能靠重画修好，而我没拿到授权」时停在这一步。
   * 对用户说「等你决定」，不说「请求人工」——决定的人就是他自己，不是我们这边有个人。
   */
  request_human: {
    label: '等你决定',
    doing: '停在这里等你决定。',
    what: '这张图只能靠深度修复才能修好，而深度修复会改变画面内容，所以停在这里等你授权。',
  },
  verify: {
    label: '验收',
    doing: '在把处理前后各量一遍，看那些纹理降了多少。',
    what: '把处理前后放在一起复诊，确认异常真的降下去了、画面没有被削平。',
  },
  finish: {
    label: '完成',
    // 「写产物、算复诊读数」是我们这边的活儿名字；用户要的是「还要等多久」。
    doing: '在整理结果，马上就好。',
    what: '产物已就绪。',
  },
}

/**
 * 把一个管线步骤翻成中文解释。
 *
 * 背景：管线的 reason 是韩语单句，信息密度高但对用户不可读；
 *   而 result 里有真实数值，可以撑起一句更具体的中文。
 * 设计意图：优先用 result 的数值生成解释，取不到数值时退回动作的通用说明。
 *   这样即使管线以后新增字段，页面也不会空白。
 * 关键约束：不编造数值。取不到就不说那个数，只给通用说明。
 *
 *   running 为真时一律只给那一步的 `doing`，绝不读 result。原因是 result 描述的是
 *   这一步做完之后的事实。处理中页面上曾出现「产物 1665×937 像素」这句话，
 *   而那一步还在跑，尺寸此刻根本不存在。真实作业里 running 步骤的 result 是空的，
 *   把它当成有值来渲染，就会在联调时才发现整句消失。
 */
export function explainStep(step: RunStep, running = false): string {
  // 管线以后新增动作时，这里取不到模板。宁可让那一步只显示服务端给的中文理由，
  // 也不能让整条时间线抛异常——那会把已经跑完的几步一起从页面上抹掉。
  const info = ACTION_INFO[step.action] as
    { label: string; doing: string; what: string } | undefined
  if (!info) return step.reason
  if (running) return info.doing
  const r = step.result as Record<string, number | string | boolean | undefined>

  switch (step.action) {
    case 'reference_clean': {
      // 走到这一步的唯一原因是叠瓦指数达到 structured，滤波清不掉
      const pct = typeof r.scale_tile_pct === 'number' ? r.scale_tile_pct : undefined
      const base = pct !== undefined
        ? `全幅叠瓦指数 ${pct}%，已达结构化。`
        : '叠瓦指数已达结构化。'
      return `${base}结构化的鳞片铺满画面，滤波器清不掉它，只能先净化出一张参考本，再据它重画一遍。`
    }
    case 'notch': {
      const bins = typeof r.notched_bins === 'number' ? r.notched_bins : undefined
      const energy = typeof r.energy_removed_pct === 'number' ? r.energy_removed_pct : undefined
      if (bins !== undefined && energy !== undefined) {
        return `切掉了 ${bins} 个孤立频点，占全图能量的 ${energy}%。动到的能量这么少，画面内容不会有可见变化。`
      }
      return info.what
    }
    case 'regenerate': {
      const size = Array.isArray(r.saved_size) ? (r.saved_size as unknown as number[]) : undefined
      const verified = r.size_verified === true
      const tail = size
        ? `产物 ${size[0]}×${size[1]} 像素${verified ? '' : '（与请求尺寸不一致，已如实记录）'}。`
        : ''
      return `${info.what}${tail}`
    }
    case 'verify': {
      if (r.passed === true) return '前后对照复诊通过，异常已降下去且画面没有被削平。'
      return '前后对照复诊未通过，原因见下方技术细节。'
    }
    case 'request_human':
      // 这一步的内容**就是**它停下的理由，而理由分岔：还没授权、授权了但重画次数
      // 用完、这一档没开重画。服务端已经按岔口算好了那句中文（core/step_reason.py），
      // 这里再写死一句只会说错——重画过一轮的那次，页面上写着「停在这里等你授权」，
      // 而用户明明已经点过同意了（2026-09-10 实跑撞到）。
      return step.reason || info.what
    case 'diagnose': {
      const lattice = r.lattice === true
      const g = typeof r.granule_level === 'string' ? (r.granule_level as GranuleLevel) : undefined
      const s = typeof r.scale_level === 'string' ? (r.scale_level as ScaleLevel) : undefined
      const parts: string[] = []
      if (lattice) parts.push('检出格子纹')
      if (g && g !== 'none') parts.push(`鳞片${GRANULE_LABEL[g].text}`)
      if (s && s !== 'none') parts.push(`叠瓦${SCALE_LABEL[s].text}`)
      if (parts.length === 0) return '三项都没超线，这张图不需要处理。'
      return `本轮结果：${parts.join('、')}。`
    }
    default:
      return info.what
  }
}

/** 这一步交出了什么：几个读数，外加它写下的文件。 */
export interface StepOutcome {
  /** 成对的读数。value 是可直接显示的字符串。 */
  readings: { k: string; v: string }[]
  /** 这一步写出来的文件：产物清单里的名字，外加我们给它起的称呼。 */
  files: { name: string; label: string }[]
}

/**
 * 一步做完之后，它到底得出了什么。
 *
 * 背景：2026-09-11 用户读完整条轨迹后说「这些最好能够给出每一步结果是怎样的」。
 *   在那之前每一步只有两样东西：一个「规则决策」的徽章，和一句为什么走这一步。
 *   管线每一步其实都留下了实测读数与文件（切了多少个频点、重画出来多大、
 *   复诊过没过、净化本长什么样），它们躺在 result 里，只有展开「技术细节」
 *   看原始 JSON 才找得到——那等于没给。
 * 设计意图：把 result 里**这一步自己测出来的**那几个数挑出来，配上它写下的文件名。
 *   文件名不带路径（服务端已经剥成文件名，见 step_reason.py 的 step_for_page），
 *   页面凭名字去产物清单里对，对得上才给链接。
 * 关键约束：
 *   1. 只报这一步的产出，不报它的输入，也不替它总结全局。整条轨迹的结论在完成页
 *      顶上那一句和三张读数卡里，这里重复一遍只会让人以为处理了两次。
 *   2. 取不到的字段就不摆。少一项读数，比摆一个「—」或者猜一个值好。
 *   3. 文件一律按名字给。这一层不知道作业 id，也就编不出下载地址——
 *      能不能点由调用方决定（真作业才有产物，录像那卷没有）。
 */
export function stepOutcome(step: RunStep): StepOutcome {
  const r = (step.result ?? {}) as Record<string, unknown>
  const readings: StepOutcome['readings'] = []
  const files: StepOutcome['files'] = []
  const num = (k: string) => (typeof r[k] === 'number' ? (r[k] as number) : undefined)
  const file = (k: string, label: string) => {
    if (typeof r[k] === 'string' && r[k]) files.push({ name: r[k] as string, label })
  }

  switch (step.action) {
    case 'diagnose': {
      readings.push({ k: '格子纹', v: r.lattice === true ? '已检出' : '未检出' })
      const g = typeof r.granule_level === 'string' ? (r.granule_level as GranuleLevel) : undefined
      if (g) readings.push({ k: '鳞片', v: GRANULE_LABEL[g]?.text ?? g })
      const s = typeof r.scale_level === 'string' ? (r.scale_level as ScaleLevel) : undefined
      const pct = num('scale_tile_pct')
      if (s) {
        readings.push({
          k: '叠瓦',
          v: pct !== undefined ? `${pct}% 的方块（${SCALE_LABEL[s]?.text ?? s}）`
            : SCALE_LABEL[s]?.text ?? s,
        })
      }
      file('board', '诊断对照板')
      file('scale_heatmap', '叠瓦热力图')
      break
    }
    case 'notch': {
      const bins = num('notched_bins')
      if (bins !== undefined) readings.push({ k: '切掉的频点', v: `${bins} 个` })
      const energy = num('energy_removed_pct')
      if (energy !== undefined) readings.push({ k: '动到的能量', v: `${energy}%` })
      const peak = num('max_peak_excess')
      if (peak !== undefined) readings.push({ k: '最强那根格子峰', v: `高出周围 ${peak} 倍` })
      file('output', '切完格子的那张')
      break
    }
    case 'reference_clean': {
      // 人脸是这一步唯一对外有意义的读数：净化会削细节，脸被单独护住
      // （管线的 face_guard_mask）。不说的话，「净化」听起来像会把人脸也磨平。
      const faces = Array.isArray(r.face_boxes) ? (r.face_boxes as unknown[]).length : undefined
      if (faces !== undefined) {
        readings.push({ k: '人脸', v: faces > 0 ? `认出 ${faces} 处，净化时单独护住` : '没认出人脸' })
      }
      file('reference', '净化出来的参考本')
      break
    }
    case 'regenerate': {
      const size = Array.isArray(r.saved_size) ? (r.saved_size as number[]) : undefined
      if (size && size.length === 2) readings.push({ k: '重画产物', v: `${size[0]}×${size[1]} 像素` })
      if (typeof r.size_verified === 'boolean') {
        readings.push({ k: '与请求的尺寸', v: r.size_verified ? '一致' : '不一致，已如实记录' })
      }
      file('output', '重画出来的那张')
      break
    }
    case 'verify': {
      const passed = r.passed === true
      readings.push({ k: '复诊', v: passed ? '通过' : '未通过' })
      // reasons 是管线写的韩语（`구조 창 [428,141,524,237] 잔차 std …`），一个字都不能
      // 端到页面上；逐窗的中文明细在下载的处理报告里（服务端拿 *_verify.json 重述）。
      // 这里只报条数——条数是数出来的，不是解读出来的。
      const why = Array.isArray(r.reasons) ? (r.reasons as unknown[]).filter(x => typeof x === 'string') : []
      if (!passed && why.length) {
        readings.push({ k: '越限的项', v: `${why.length} 项，逐项读数见验收对照板与处理报告` })
      }
      file('board', '验收对照板')
      break
    }
    default:
      break
  }
  return { readings, files }
}

/*
 * 这里曾有一个 summarize()：按严重度挑一条，生成「这张图怎么了、打算怎么办」的
 * 一句话，供结果页顶部那张结论条使用。2026-09-11 用户裁决「顶部的那个卡片真的
 * 不太好用」「彻底不要」之后，处理前的那一句由闸来说——闸上写的是「接下来会对这张图
 * 做什么」（见 RipplePage 的 planOf 与 Gates 的 ConfirmGate/RegenGate），
 * 比描述病情更接近用户要拍的板，而三项指标各自的严重度在诊断卡的徽章上逐项写着。
 * 完成页那一句仍然要（说的是已经发生的变化），见下面的 summarizeOutcome。
 */

/** 一项指标的前后变化。字段都是可直接显示的字符串。 */
export interface MetricDelta {
  key: 'lattice' | 'granule' | 'scale'
  label: string
  before: string
  after: string
  /** 这一项是否降到了阈值以内 */
  cleared: boolean
  /** 徽章文案与色调。色调由本层决定，组件不再推断策略。 */
  badge: string
  tone: 'none' | 'info' | 'warn'
  /** 有改善但没归零时的补充说明，没有则不显示 */
  caveat?: string
}

/**
 * 处理完成后的结论。与 summarize() 是两个口径，不能互相替代。
 *
 * 背景：summarize() 描述的是「这张图怎么了、打算怎么办」，句式是「需要…」「可以…」。
 *   把它用在完成页上，会把已经做完的动作说成待办。更糟的是：格子切除做完之后
 *   lattice.detected 并不归零（实测 E20-K：最强尖峰 4.348 → 2.996，保留分量
 *   1164 → 578，但 2 像素量级的周期峰仍在阈值之上），于是 summarize() 会在成品上
 *   输出「检出周期性格子纹，可以无损切除」，把一个已完成的步骤重新提出来。
 * 设计意图：完成页只报实际发生的变化，每一项都给出前后两个实测值。
 *   降到线内的说「已清除」，只是变小的说「已压低」并写明仍看得出来。
 *   宁可让用户看到「削弱但仍检出」，也不谎报清零，因为他会自己拿图去复检。
 * 关键约束：不比较不同图上的数值。传入的 after 必须是页面上展示的那张成品自己的诊断。
 *
 * 返回的两样东西落在页面的两处（2026-09-12 起）：
 *   · `headline`——图底下那一句「变成了什么样」，紧挨着下载按钮。
 *   · `deltas`——并进下面那三张指标卡的卡头（`DiagnosisCards` 的 `deltas`）。
 *     此前它们自成一排卡贴在图底下，于是同一项指标在一页上有两张卡、两种顺序、
 *     两套名字，用户画了个叉：「排序还不太对」「能不能上下合并一下」「感觉有点重复」。
 *     这里曾另有一个组件 `Readings.tsx`（`OutcomeReadings`）专管那一排卡，
 *     合并之后它只剩一行标题，所以删了。
 */
export function summarizeOutcome(
  before: Diagnosis,
  after: Diagnosis,
): { headline: string; detail: string; severity: Severity; deltas: MetricDelta[] } {
  const scaleCleared = after.scale_index.level === 'none'
  const granuleCleared = after.granule.level === 'none'
  const latticeCleared = !after.lattice.detected

  // 处理前就已达标的项不说「已清除」。
  //
  // 只走格子切除的那一档里，叠瓦本来就是 0%，说「已清除」等于宣称清掉了不存在的东西。
  // 用户会去找我们到底动了哪里，找不到就开始怀疑其余数字。
  const scaleWasClean = before.scale_index.level === 'none'
  const granuleWasClean = before.granule.level === 'none'

  const clearedBadge = (wasClean: boolean) => (wasClean ? '本来就干净' : '已清除')

  // 次序与称呼都取自 METRIC_ORDER / METRIC_LABEL：这几张卡与诊断那几张是同一批指标，
  // 在页面上已经合成一张卡了（见 DiagnosisCards），两处不能各排一个顺序。
  const deltas: MetricDelta[] = [
    {
      key: 'scale',
      label: METRIC_LABEL.scale,
      before: `${before.scale_index.scale_tile_pct}%（${SCALE_LABEL[before.scale_index.level].text}）`,
      after: `${after.scale_index.scale_tile_pct}%（${SCALE_LABEL[after.scale_index.level].text}）`,
      cleared: scaleCleared,
      badge: scaleCleared ? clearedBadge(scaleWasClean) : '还没降下来',
      tone: scaleCleared ? (scaleWasClean ? 'info' : 'none') : 'warn',
    },
    {
      key: 'granule',
      label: METRIC_LABEL.granule,
      before: `${GRANULE_LABEL[before.granule.level].text}，${before.granule.flagged_windows} 处`,
      after: `${GRANULE_LABEL[after.granule.level].text}，${after.granule.flagged_windows} 处`,
      cleared: granuleCleared,
      badge: granuleCleared ? clearedBadge(granuleWasClean) : '还没降下来',
      tone: granuleCleared ? (granuleWasClean ? 'info' : 'none') : 'warn',
    },
    {
      key: 'lattice',
      // 「尖峰」「分量」是频域的说法，页面上换成人话（2026-09-11）；数值不动。
      label: METRIC_LABEL.lattice,
      before: `明显度 ${before.lattice.max_peak_excess}，${before.lattice.isolated_components_kept} 处`,
      after: `明显度 ${after.lattice.max_peak_excess}，${after.lattice.isolated_components_kept} 处`,
      cleared: latticeCleared,
      badge: latticeCleared ? '已清除' : '已压低',
      // 削弱是这一项的预期终点，不是未达标。用中性色，避免在绿色结论里插一个橙色徽章，
      // 那会让人以为出了问题而去反复重跑。
      tone: latticeCleared ? 'none' : 'info',
      // 说清「为什么没归零」就够，不必连采样极限一起讲一遍
      // （2026-09-11「有点太啰嗦了」：这一格原先是三句，占的地方比它解释的两个数还多）。
      caveat: latticeCleared
        ? undefined
        : `${after.lattice.peaks_top[0]?.period_px ?? '—'} 像素那一档的网格还看得出来：`
          + '格眼只有两三个像素，已经和画面本身的细节挨在一起，再切就会把细节一起磨掉，'
          + '所以只压低、不追求清零。',
    },
  ]

  // 严重度只看叠瓦与鳞片这两项。
  //
  // 格子纹的检出标志在成品上不会归零：2 像素量级的周期紧贴采样极限，切得再深就要动到
  // 画面本身的细节。两次真跑都印证了这一点（链 K 峰值 4.348 → 2.996、链 C 3.097，
  // 两者 detected 都仍为 true）。若把它算进严重度，那么每一个完成页都会是告警色，
  // 而恒亮的告警不携带信息，用户会先学会忽略它，再在真的出问题时也一起忽略。
  // 所以格子残留只在它自己那张卡里说明，不改变整条结论的颜色。
  const actionable = deltas.filter(d => d.key !== 'lattice')
  const allCleared = actionable.every(d => d.cleared)
  const scaleDropped = before.scale_index.scale_tile_pct - after.scale_index.scale_tile_pct

  if (allCleared && scaleDropped > 1) {
    return {
      severity: 'none',
      headline: `处理完成，叠瓦指数从 ${before.scale_index.scale_tile_pct}% 降到 ${after.scale_index.scale_tile_pct}%`,
      detail: '成片的鳞片纹理已经消失，平坦的地方恢复干净。那层细网格也压低了，逐项对照见下。',
      deltas,
    }
  }

  if (allCleared) {
    return {
      severity: 'none',
      headline: `处理完成，格子纹的明显度从 ${before.lattice.max_peak_excess} 压到 ${after.lattice.max_peak_excess}`,
      detail: `要清的地方从 ${before.lattice.isolated_components_kept} 处降到 `
        + `${after.lattice.isolated_components_kept} 处。鳞片和叠瓦两项处理前后都在线内。`
        + '这一档只挑出那层网格清掉，画面内容和尺寸都没动过。',
      deltas,
    }
  }

  return {
    severity: 'warn',
    headline: '处理完成，但还有一项没降到线内',
    detail: '下面是处理前后的逐项对照，每个数都是在对应的那张图上重新量的。没降下来的那项已经标出来了。',
    deltas,
  }
}

/**
 * 一轮的叫法。0 是用户上传的那张，之后每一条是重画一轮之后的复诊。
 *
 * 不叫「第 0 轮」：0 轮那件事在用户这边的名字是「处理前」，它与完成页那一列
 * （见 summarizeOutcome 的 before）说的是同一张图，两处要同名。
 */
export function roundLabel(round: number): string {
  return round === 0 ? '处理前' : `重画 ${round} 轮后`
}

/**
 * 这几轮读数摊出来之后，还要补的那一句话。
 *
 * 2026-09-13 用户实撞：一趟重画了两轮仍未达标的作业，屏上只有处理前那个 36.4%
 * （服务端那句话引的是第一份诊断，已经改成引最近一份），而实测三份读数是
 * 36.4% → 29.4% → 11.4%——一路在往下走，只是撞了轮数上限。用户读完屏上那句
 * 「重画之后仍在结构化线上 36.4%」，问的是「这是搞不定吗」。
 *
 * 所以这一句只回答那个问题，判断只做一件：**每一轮都在降吗**。降就说出来
 * （它是「再来一轮」值不值的唯一依据），没在降也要说出来——那时再花钱多半是白花。
 * 不预测下一轮会落到多少：出图有随机性，管线自己也只是一轮一轮量。
 */
export function roundsTrend(rounds: RoundReading[]): string | null {
  const pts = rounds
    .filter(r => typeof r.scale_pct === 'number')
    .sort((a, b) => a.round - b.round) as (RoundReading & { scale_pct: number })[]
  if (pts.length < 2) return null
  const line = pts.find(r => typeof r.scale_line === 'number')?.scale_line
  const tail = line != null ? `，判定线是 ${line}%` : ''
  const dropping = pts.every((p, i) => i === 0 || p.scale_pct < pts[i - 1].scale_pct)
  if (dropping) {
    return `每一轮都比上一轮低，从 ${pts[0].scale_pct}% 一路降到 ${pts[pts.length - 1].scale_pct}%`
      + `${tail}——是重画次数用完了，不是压不下去。`
  }
  const best = Math.min(...pts.map(p => p.scale_pct))
  return `重画到现在最低降到 ${best}%${tail}，但并不是每一轮都在降。`
    + '这类画面里的纹理和内容长在一起，再重画未必更好。'
}
